import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useLayout } from '@/providers/Layout.provider';
import { useToken } from '@/providers/Token.provider';
import styles from './MultiplePaymentMethods.module.scss';
import PaymentMethods from '../PaymentMethods/PaymentMethods';
import { useFlexiblePayment } from '@/providers/FlexiblePayment.provider';
import MultiplePaymentMethodsDialogue from './Dialogue/MultiplePaymentMethodsDialogue';
import { usePaylink } from '@/providers/Paylink.provider';
import usePostPaylinkSetAccount from '@/hooks/useApi/usePostPaylinkSetAccount';

const { Box, Title, Tooltip, Button } = vp;

type FC = FunctionComponent;

const MultiplePaymentMethods: FC = () => {
  const { setLayoutInfo, color } = useLayout();
  const { token, isSetByBrowser } = useToken();
  const { updateFlexiblePayment, addPaymentMethod, paymentMethods } = useFlexiblePayment();
  const history = useHistory();
  const [showSetAmount, setShowSetAmount] = useState(false);
  const [connectionMethod, setConnectionMethod] = useState<vp.ConnectionTypes>();
  const [amount, setAmount] = useState<string>('');
  const [availableAmount, setAvailableAmount] = useState<string>('');
  const [setAccountResponse, setAccount] = usePostPaylinkSetAccount({});
  const { paylink, Token } = usePaylink();

  useEffect(() => {
    if (setAccountResponse && setAccountResponse.Success) {
      history.push('/thank-you');
    } else if (setAccountResponse && !setAccountResponse.Success) {
      history.push('/connect/transaction-error');
    }
  }, [setAccountResponse]);

  useEffect(() => {
    if (paylink) {
      setAmount(paylink.Amount);
      const sumUsedAmount = paymentMethods.reduce((acc, method) => acc + method.amount, 0);
      setAvailableAmount((Number(vp.sanitizeToNumeric(paylink.Amount)) - sumUsedAmount).toString());
    }
  }, [paylink, paymentMethods]);
  const { t } = useTranslation('translations', { keyPrefix: 'MultiplePaymentMethods' });
  useEffect(() => {
    setLayoutInfo(
      vp.chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
      'curve',
      '',
      70,
      32,
      0,
      !isSetByBrowser,
      true,
    );
  }, []);
  const SubmitPayment = () => {
    if (Number(availableAmount) === 0) {
      const paymentsToSend = paymentMethods.filter((method) => method.amount > 0 && !!method.token);
      if (paymentsToSend.length > 0) {
        const formattedPayments = paymentsToSend.reduce(
          (acc, method) => ({
            ...acc,
            [method.token]: {
              Amount: method.amount,
              ConnectionType: method.method,
            },
          }),
          {},
        );

        setAccount({
          SingleUseToken: Token,
          PaymentMethod: 'bank', // when PaymentOptions is 'multiple', PaymentMethod is ignored
          Iq11Token: Object.keys(formattedPayments)[0],
          FlexiblePaymentOptions: {
            PaymentOption: 'multiple',
            Tokens: formattedPayments,
          },
        });
      }
    }
  };
  const onSaveAmount = (amountSaved: string) => {
    if (connectionMethod) {
      updateFlexiblePayment(true, vp.FlexiblePaymentMethods.MultiplePaymentMethods);

      addPaymentMethod({
        amount: Number(vp.sanitizeToNumeric(amountSaved)),
        method: connectionMethod || vp.ConnectionTypes.Bank,
        token: '',
        account: '',
      });
      setShowSetAmount(false);
      if (connectionMethod === vp.ConnectionTypes.Bank) {
        history.push({
          pathname: '/connect/processor/bank',
          state: { origin: 'token' },
        });
      } else if (connectionMethod === vp.ConnectionTypes.Credit) {
        history.push({
          pathname: '/connect/processor/credit',
          state: { origin: 'token' },
        });
      } else if (connectionMethod === vp.ConnectionTypes.DebitCard) {
        history.push({
          pathname: '/connect/processor/debitcard',
          state: { origin: 'token' },
        });
      } else if (connectionMethod === vp.ConnectionTypes.Email) {
        history.push({
          pathname: '/connect/processor/email',
          state: { origin: 'token' },
        });
      }
    }
  };

  return (
    <Box jc="center">
      <Box flx="col" ai="center" jc="center" base="8" gap="md" maxwidth="545px">
        <Box pt="lg" base="8" gap="sm" ai="center" height="auto">
          <Title title={`${t('title')}`} size="lg" bold />
          <Tooltip
            information={`${t('multiplePaymentMethodsDescription')}`}
            icon="question"
            position="left"
            variant="outlined"
            fontSize="16px"
            textAlign="left"
            positionX="-100%"
            positionY="-46%"
            width="234px"
          />
        </Box>
        <Box base="8" gap="md" flx="col" height="auto">
          <Box jc="center">
            <Box gapSize="10px" base="5" flx="col" boundary="shadow" p="md" r="md" maxwidth="280px" width="280px">
              <div>
                <div className={styles.totalTitle}>{`${t('total')}`}</div>
                <div className={styles.totalAmount}>
                  {paylink.Currency} {vp.formattedCurrency(amount)}
                </div>
              </div>
              <div className={styles.totalRemaining}>
                {`${t('remaining')}`} {paylink.Currency}{' '}
                {vp.formattedCurrency(vp.sanitizeToNumeric(availableAmount) || '')}
              </div>
            </Box>
          </Box>
          <Box ai="center" jc="center" width="100%">
            <div className={styles.selectionText}>{`${t('selection')}`}</div>
          </Box>
          <PaymentMethods
            flexiblePaymentMethod={vp.FlexiblePaymentMethods.MultiplePaymentMethods}
            onClick={(connectionType: vp.ConnectionTypes) => {
              switch (connectionType) {
                case vp.ConnectionTypes.Bank:
                  setShowSetAmount(true);
                  setConnectionMethod(vp.ConnectionTypes.Bank);
                  break;
                case vp.ConnectionTypes.Credit:
                  setShowSetAmount(true);
                  setConnectionMethod(vp.ConnectionTypes.Credit);
                  break;
                case vp.ConnectionTypes.DebitCard:
                  setShowSetAmount(true);
                  setConnectionMethod(vp.ConnectionTypes.DebitCard);
                  break;
                case vp.ConnectionTypes.Email:
                  setShowSetAmount(true);
                  setConnectionMethod(vp.ConnectionTypes.Email);
                  break;
                default:
                  break;
              }
            }}
          />
          <Button
            label={
              <div style={{ height: '48px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{`${t(
                'continue',
              )}`}</div>
            }
            color="primary"
            opacity={Number(availableAmount) === 0 ? '1' : '0.5'}
            isDisabled={Number(availableAmount) !== 0}
            id="multiple-payment-method-continue"
            background={color}
            textColor={vp.getContrastColor(color)}
            height="48px"
            round
            onClick={() => {
              SubmitPayment();
            }}
          />
          <MultiplePaymentMethodsDialogue
            show={showSetAmount}
            onSave={(_values: string) => {
              onSaveAmount(_values);
            }}
            onClose={() => {
              setShowSetAmount(false);
            }}
            totalAmount={amount}
            remainingAmount={availableAmount}
            defaultValues={{ Amount: '', MaximumAmount: availableAmount }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default MultiplePaymentMethods;
